import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, SlidersHorizontal, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { ProfileCard, MatchCard } from "@/components/feed/ProfileCard";

const CHIPS = ["Aceita pets", "Vegetariana", "Organizada", "Fumante", "Não fumante", "Muitas Visitas"];
const SORTS = ["Mais compatível", "Mais recentes"];

export default function Feed() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [activeChips, setActiveChips] = useState([]);
  const [sort, setSort] = useState(SORTS[0]);
  const [sortOpen, setSortOpen] = useState(false);
  const [visible, setVisible] = useState(6);
  const [dismissed, setDismissed] = useState([]);
  const chipScroll = useRef(null);

  const { data, isLoading } = useQuery({ queryKey: ["feed"], queryFn: async () => (await api.get("/feed")).data });

  const likeMut = useMutation({
    mutationFn: async (candidate_id) => (await api.post("/likes", { candidate_id })).data,
    onSuccess: (res, candidate_id) => {
      if (res.matched) toast.success(`Deu match com ${res.candidate.full_name}! 🎉`);
      else toast.success("Curtida enviada!");
      qc.invalidateQueries({ queryKey: ["feed"] });
    },
    onError: (err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)),
  });

  const toggleChip = (c) => setActiveChips((a) => a.includes(c) ? a.filter((x) => x !== c) : [...a, c]);

  let items = (data?.items || []).filter((i) => !dismissed.includes(i.id));
  if (sort === "Mais compatível") items = [...items].sort((a, b) => (b.compatibility || 0) - (a.compatibility || 0));
  const shown = items.slice(0, visible);

  const scrollChips = (dir) => chipScroll.current?.scrollBy({ left: dir * 200, behavior: "smooth" });

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1 mc-container py-10" data-testid="page-feed">
        <h1 className="text-3xl md:text-4xl font-bold text-[#1e1b2e]">Encontre seu match</h1>

        {/* Filter chips carousel */}
        <div className="flex items-center gap-2 mt-6">
          <button onClick={() => scrollChips(-1)} className="p-1 text-muted-foreground shrink-0" data-testid="chips-prev"><ChevronLeft size={20} /></button>
          <div ref={chipScroll} className="flex gap-3 overflow-x-auto scrollbar-hide py-1" style={{ scrollbarWidth: "none" }}>
            {CHIPS.map((c) => (
              <button key={c} className="mc-chip" data-active={activeChips.includes(c)} data-testid={`chip-${c}`} onClick={() => toggleChip(c)}>{c}</button>
            ))}
          </div>
          <button onClick={() => scrollChips(1)} className="p-1 text-muted-foreground shrink-0" data-testid="chips-next"><ChevronRight size={20} /></button>
        </div>

        {/* Filtros / Ordenar */}
        <div className="flex items-center justify-between mt-5">
          <button className="flex items-center gap-2 text-sm font-semibold text-[#1e1b2e]" data-testid="feed-filtros"><SlidersHorizontal size={16} /> Filtros</button>
          <div className="relative">
            <button className="flex items-center gap-2 text-sm font-semibold text-[#1e1b2e]" data-testid="feed-ordenar" onClick={() => setSortOpen((o) => !o)}>{sort} <ChevronDown size={16} /></button>
            {sortOpen && (
              <div className="absolute right-0 mt-2 bg-white border border-[#eeebf7] rounded-xl shadow-lg z-10 w-48 py-1" data-testid="sort-menu">
                {SORTS.map((s) => (
                  <button key={s} className="block w-full text-left px-4 py-2 text-sm hover:bg-[#f7f5fd]" data-testid={`sort-${s}`} onClick={() => { setSort(s); setSortOpen(false); }}>{s}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="feed-loading">Carregando perfis…</div>
        ) : shown.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="feed-empty">Nenhum perfil disponível no momento.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            {shown.map((p) => p.matched ? (
              <MatchCard key={p.id} profile={p}
                onMessage={() => navigate(p.conversation_id ? `/chat?c=${p.conversation_id}` : "/chat")}
                onContinue={() => setDismissed((d) => [...d, p.id])} />
            ) : (
              <ProfileCard key={p.id} profile={p} liking={likeMut.isPending} onLike={(pr) => likeMut.mutate(pr.id)} />
            ))}
          </div>
        )}

        {visible < items.length && (
          <div className="text-center mt-10">
            <button className="btn-outline" data-testid="feed-vermais" onClick={() => setVisible((v) => v + 6)}>Ver mais</button>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
